import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import useCMSContent from '../hooks/useCMSContent';

const AboutPage = () => {
  const cms = useCMSContent('about');
  const seoData = {
    title: 'About Visser General Construction LLC | Connecticut Home Remodeling Since 2022',
    description: 'Meet Cody Visser and learn about Visser General Construction. Established 2022, serving Connecticut homeowners with quality craftsmanship. Meriden-based contractor.',
    keywords: 'Visser General Construction, Cody Visser contractor, Meriden CT contractor, Connecticut home remodeling company',
    canonical: 'https://cthomeremodels.com/about',
    ogImage: 'https://cthomeremodels.com/og/v2/about.png'
  };

  return (
    <div className="about-page">
      <SEO {...seoData} />
      
      {/* Hero */}
      <section 
        className="relative py-32 md:py-40 bg-cover bg-center bg-no-repeat" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url('/api/media/name/driveway-repair-maintenance-connecticut-home-services.jpg')`
        }}
      >
        <div className="absolute top-8 left-8">
          <img 
            src="/api/media/name/visser-general-construction-llc-logo-connecticut.png"
            alt="Visser General Construction LLC Logo - Licensed and Insured Connecticut Home Remodeling"
            className="h-24 md:h-32 object-contain drop-shadow-2xl"
          />
        </div>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg">About Us</h1>
          <p className="text-xl max-w-3xl mx-auto text-white drop-shadow-md">
            {cms('hero_subtitle', 'Quality craftsmanship and exceptional service since 2022')}
          </p>
        </div>
      </section>

      {/* Owner Info */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="/api/media/name/cody-visser-owner-general-contractor-connecticut.png"
                  alt="Cody Visser - Owner of Visser General Construction LLC, Licensed and Insured Connecticut Home Remodeling Contractor"
                  className="rounded-xl shadow-2xl w-full"
                  data-testid="owner-photo"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold mb-6">{cms('owner_title', 'Meet Cody Visser')}</h2>
                <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                  {cms('owner_p1', 'Owner and founder of Visser General Construction LLC, Cody brings passion for quality craftsmanship and a commitment to customer satisfaction to every project.')}
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {cms('owner_p2', 'Based in Meriden, CT, Cody established Visser General Construction in 2022 with a simple mission:')}
                  <strong className="text-[#5c0701]"> "{cms('owner_mission', 'Accomplish the best quality work possible.')}"</strong>
                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {cms('owner_p3', 'With expertise in kitchen and bathroom remodeling, deck construction, basement finishing, and more, Visser General Construction has quickly become a trusted name for homeowners throughout Connecticut.')}
                </p>
                <div className="space-y-3">
                  <p className="flex items-center gap-3 text-lg">
                    <svg className="w-6 h-6 text-[#3a87fe]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <a href="tel:2036043202" className="hover:text-[#3a87fe] transition">(203) 604-3202</a>
                  </p>
                  <p className="flex items-center gap-3 text-lg">
                    <svg className="w-6 h-6 text-[#3a87fe]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span>42 Gay St, Meriden, CT</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Visser General Construction LLC</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-bold mb-4 text-[#5c0701]">Our Services</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#3a87fe]">✓</span> Kitchen Remodels
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3a87fe]">✓</span> Bathroom Remodels
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3a87fe]">✓</span> Trex Decking Install
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3a87fe]">✓</span> Finished Basements
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3a87fe]">✓</span> Vinyl Siding
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3a87fe]">✓</span> Window Replacement
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3a87fe]">✓</span> Custom Furniture
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3a87fe]">✓</span> General Home Maintenance
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md">
                <h3 className="text-2xl font-bold mb-4 text-[#5c0701]">Contact Information</h3>
                <div className="space-y-4 text-gray-700">
                  <div>
                    <p className="font-semibold">Address:</p>
                    <p>42 Gay St<br />Meriden, CT</p>
                  </div>
                  <div>
                    <p className="font-semibold">Phone:</p>
                    <p><a href="tel:2036043202" className="text-[#3a87fe] hover:underline">(203) 604-3202</a></p>
                  </div>
                  <div>
                    <p className="font-semibold">Hours:</p>
                    <p>Monday - Friday<br />5:00 AM - 5:30 PM</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <h3 className="text-2xl font-bold mb-4 text-[#5c0701]">Service Area</h3>
              <p className="text-lg text-gray-700 mb-4">
                {cms('service_area', 'Proudly serving homeowners within a 40-mile radius of Meriden, CT')}
              </p>
              <p className="text-gray-600">
                {cms('service_area_cities', 'Including Wallingford, Southington, Cheshire, Hamden, Old Saybrook, Madison, Guilford, Branford, Middletown, and surrounding communities')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#3a87fe] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">{cms('cta_title', 'Let\'s Work Together')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            {cms('cta_subtitle', 'Ready to transform your home? Get in touch for a free consultation')}
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-[#3a87fe] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition"
            data-testid="about-cta-contact"
          >
            Contact Us Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;